import React, {ComponentProps, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import ReactFlow from 'react-flow-renderer';
import {useNavigate} from 'react-router-dom';
import {useParams} from 'react-router';
import {useTranslation} from 'react-i18next';

import {combineLatest, map, of} from 'rxjs';
import {Button, Checkbox, Input, useTheme} from '@innowise-group/ui-kit';
import {ButtonHelper, ExportFiles} from '@shared-components';
import {
  AttendanceStatistics,
  Can,
  Employee,
  EmployeeWorkspace,
  PersonContext,
  RequestOnPlan,
  useService,
} from '@innowise-group/core';
import {subject} from '@casl/ability';

import * as Styled from './floor-details.styles';
import {BookingWorkspaceModeForm, BookingWorkspaceModeFormValues} from '../../components/booking-workspace-mode-form';
import {EmployeesList} from '../../components/employees-list';
import {FloorData} from './use-plan-details.hook';
import {FloorPlan, FloorPlanHandler} from '../../components/floor-plan';
import {FloorSelector} from '../../components/floor-selector';
import {FloorAttendance} from '../../components/floor-attendance';
import {FloorsModalsFacadeService} from '../../services/floors-modals-facade';
import {SearchFloorForm, SearchFloorValues} from '../../components/search-floor-form';

interface FloorDetailsProps {
  Map?: React.FC<ComponentProps<typeof ReactFlow> & {isEditMode: boolean}>;
  requestData?: RequestOnPlan;
  focusWorkspace?: Pick<EmployeeWorkspace, 'id' | 'floorId'>;
}

const FloorDetails: React.FC<FloorDetailsProps> = ({requestData, focusWorkspace, Map = ReactFlow}) => {
  const theme = useTheme();
  const [isEditMode, setIsEditMode] = useState(false);
  const [isShowSecondPlan, setIsShowSecondPlan] = useState(false);
  const [floorData, setFloorData] = useState<FloorData>({} as FloorData);
  const [isPlanExists, setIsPlanExists] = useState(false);
  const [focus, setFocus] = useState(true);
  const [searchName, setSearchName] = useState('');
  const [secondFloorId, setSecondFloorId] = useState('');
  const [secondFloorData, setSecondFloorData] = useState<FloorData | null>(null);
  const [page, setPage] = useState(1);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [selectedBookingDetails, setSelectedBookingDetails] = useState<BookingWorkspaceModeFormValues>(null);
  const [formSelectedOfficeId, setFormSelectedOfficeId] = useState<string | undefined>();
  const [isFirstPlanLoading, setIsFirstPlanLoading] = useState<boolean>(false);
  const [isSecondPlanLoading, setIsSecondPlanLoading] = useState<boolean>(false);
  const [isFirstAttendanceLoading, setIsFirstAttendanceLoading] = useState<boolean>(true);
  const [isSecondAttendanceLoading, setIsSecondAttendanceLoading] = useState<boolean>(true);
  const [isWorkspaceMutating, setIsWorkspaceMutating] = useState(false);
  const [hasDraftWorkspace, setHasDraftWorkspace] = useState(false);
  const draftFlagsRef = useRef({first: false, second: false});

  const handleFirstPlanDraftStateChanged = useCallback((hasDraft: boolean) => {
    draftFlagsRef.current.first = hasDraft;
    setHasDraftWorkspace(draftFlagsRef.current.first || draftFlagsRef.current.second);
  }, []);

  const handleSecondPlanDraftStateChanged = useCallback((hasDraft: boolean) => {
    draftFlagsRef.current.second = hasDraft;
    setHasDraftWorkspace(draftFlagsRef.current.first || draftFlagsRef.current.second);
  }, []);
  const [attendanceStatistics, setAttendanceStatistics] = useState({
    attendance_percentage: 0,
    rooms: [],
    free_workspaces: 0,
  } as AttendanceStatistics);
  const [attendanceStatisticsSecondPlan, setAttendanceStatisticsSecondPlan] = useState({
    attendance_percentage: 0,
    rooms: [],
    free_workspaces: 0,
  } as AttendanceStatistics);
  const {t} = useTranslation();
  const {id: floorId} = useParams();
  const navigate = useNavigate();
  const floorsModalsFacade = useService(FloorsModalsFacadeService);
  const firstPlanRef = useRef<FloorPlanHandler>(null);
  const secondPlanRef = useRef<FloorPlanHandler>(null);
  const handleRef = useRef<FloorPlanHandler>(null);
  const person = useContext(PersonContext);

  const closeDisplayingSecondPlan = () => {
    setIsShowSecondPlan(false);
    setSecondFloorId('');
    draftFlagsRef.current.second = false;
    setHasDraftWorkspace(draftFlagsRef.current.first);
  };

  const handleSearchFloorSubmit = (values: SearchFloorValues) => firstPlanRef.current?.handleSearchFloorSubmit(values);

  const handleSecondPlanFloorChange = ({floor: floorId}: SearchFloorValues) => {
    setSecondFloorId(floorId);
    secondPlanRef.current?.handleSecondPlanFloorChange();
  };

  // «Сохранить» applies the deletions made in the editor, so it may wait for the server (HTLGT-2139 п. 10).
  // Every plan is checked before any of them sends its deletions.
  const isSaveChanges = () => {
    const plans = [firstPlanRef.current, secondPlanRef.current].filter(Boolean);
    if (!plans.every((plan) => plan.canSaveChanges())) {
      return of(false);
    }
    return plans.length
      ? combineLatest(plans.map((plan) => plan.saveChanges())).pipe(map((results) => results.every(Boolean)))
      : of(true);
  };

  const handleAbortChanges = () => {
    const requests = [
      firstPlanRef.current?.abortChanges?.() ?? of(undefined),
      isShowSecondPlan ? secondPlanRef.current?.abortChanges?.() ?? of(undefined) : of(undefined),
    ];
    return combineLatest(requests);
  };

  const handleCancelClick = () => {
    const exitEditMode = () => {
      setIsEditMode(false);
      setPage(1);
      closeDisplayingSecondPlan();
      setSelectedBookingDetails((prev) => (prev?.bookingMode ? {startDay: '', endDay: '', bookingMode: false} : null));
    };

    handleAbortChanges().subscribe({
      next: exitEditMode,
      error: exitEditMode,
    });
  };

  const handleUploadPlan = () => firstPlanRef.current?.handleUploadPlan();
  const handleBookingWorkspaceMode = (values: BookingWorkspaceModeFormValues) => {
    if ((values.bookingMode && values.startDay && values.endDay) || !values.bookingMode) {
      setSelectedBookingDetails(values);
    }
  };

  const handleAddNewWorkspace = useCallback(() => {
    const isVirtualFloor = secondFloorId && !focus ? secondFloorData?.isVirtual : floorData?.isVirtual;
    const activePlan = secondFloorId && !focus ? secondPlanRef.current : firstPlanRef.current;
    activePlan?.handleAddNewWorkspace(isVirtualFloor);
  }, [floorData?.isVirtual, focus, secondFloorData?.isVirtual, secondFloorId]);

  const handleBookWorkspace = () => firstPlanRef.current?.handleBookWorkspace();
  const handleDownloadCSV = useCallback(() => handleRef.current.downloadCSV(), []);
  const handleDownloadPDF = useCallback(() => handleRef.current.downloadPDF(), []);
  const handleDownloadXLSX = useCallback(() => handleRef.current.downloadXLSX(), []);

  useEffect(() => {
    focus ? (handleRef.current = firstPlanRef.current) : (handleRef.current = secondPlanRef.current);
  }, [focus]);

  useEffect(() => {
    if (floorData?.address) {
      setFormSelectedOfficeId(floorData.address);
    }
  }, [floorData]);

  const handleEditToggle = () => {
    if (!isEditMode) {
      // Entering edit: clear booking visualization only if booking mode was active.
      setIsEditMode(true);
      setSelectedBookingDetails((prev) => (prev?.bookingMode ? {startDay: '', endDay: '', bookingMode: false} : null));
      return;
    }

    // Exiting edit via Save: stay in edit mode if a plan could not save.
    isSaveChanges().subscribe((saved) => {
      if (!saved) {
        return;
      }
      setIsEditMode(false);
      setSelectedBookingDetails((prev) => (prev?.bookingMode ? {startDay: '', endDay: '', bookingMode: false} : null));
      closeDisplayingSecondPlan();
      setPage(1);
    });
  };

  const handleSearchName = (event: React.FormEvent<HTMLOrSVGElement & HTMLInputElement>) => {
    setSearchName(event.currentTarget.value);
    setPage(1);
  };

  const handleShowSecondPlan = () => {
    if (!isShowSecondPlan) {
      floorsModalsFacade.showSecondPlanForm(t('modals.chooseOffice'), person.allowed_locations).subscribe((value) => {
        setIsShowSecondPlan(true);
        setSecondFloorId(value.floor);
      });
    } else {
      setFocus(true);
      closeDisplayingSecondPlan();
    }
  };

  const handleNavigateBack = useCallback(() => {
    const floorOfficeId = formSelectedOfficeId || floorData?.address || localStorage.getItem('savedOfficeId');
    navigate(`/offices/${floorOfficeId}`);
  }, [floorData.address, formSelectedOfficeId, navigate]);

  const focusOnFirstPlan = useCallback((floor: FloorData) => {
    setFocus(true);
    setFloorData(floor);
  }, []);
  const focusOnSecondPlan = useCallback((floor: FloorData) => {
    setFocus(false);
    setSecondFloorData(floor);
  }, []);

  const buttons = useMemo(() => {
    return [
      {callback: handleDownloadCSV, buttonName: 'downloadCSVReport'},
      {callback: handleDownloadPDF, buttonName: 'downloadPDFReport'},
      {callback: handleDownloadXLSX, buttonName: 'downloadXLSXReport'},
    ];
  }, [handleDownloadCSV, handleDownloadPDF, handleDownloadXLSX]);

  const allowNotifications = useCallback(() => {
    if (person?.allowed_locations) {
      const floor = secondFloorId && !focus ? secondFloorData : floorData;
      return person?.allowed_locations[floor?.country]?.some((id) => id === Number(floor?.address));
    }
  }, [person?.allowed_locations, secondFloorId, focus, secondFloorData, floorData]);

  return (
    <Styled.FloorDetailsContainer
      title={
        !requestData?.showOnPlan &&
        !focusWorkspace && (
          <Styled.FloorTitleContainer>
            <Styled.FloorTitleRow className="space-between">
              <ButtonHelper text={t('buttons.back')} icon="u_angle-left-b" noExtraMargin onClick={handleNavigateBack} />
              {isPlanExists && (
                <Can I="UPDATE" this={subject('FLOOR', {id: floorId})}>
                  <ExportFiles buttons={buttons} helperColor="#3179E3" />
                </Can>
              )}
            </Styled.FloorTitleRow>
            {isPlanExists && (
              <Styled.FloorTitleRow className="float">
                {!isEditMode ? (
                  <Styled.SearchFloorFormContainer>
                    {floorData?.floor && (
                      <SearchFloorForm
                        onSubmit={handleSearchFloorSubmit}
                        onAddressChanged={setFormSelectedOfficeId}
                        initialValues={floorData}
                      />
                    )}
                  </Styled.SearchFloorFormContainer>
                ) : (
                  <Styled.SearchEmployeeContainer>
                    <Input placeholder={t('placeholders.search')} onChange={handleSearchName} />
                  </Styled.SearchEmployeeContainer>
                )}
                {!isEditMode && (
                  <Styled.BookingWorkspaceFormContainer>
                    <BookingWorkspaceModeForm onSubmit={handleBookingWorkspaceMode} />
                  </Styled.BookingWorkspaceFormContainer>
                )}
                <Styled.ActionButtonsContainer>
                  {isEditMode && (
                    <>
                      <Can I="CREATE-PLAN" this={subject('FLOOR', {id: floorId})}>
                        <Button onClick={handleUploadPlan} icon="u_arrow-up">
                          {t('buttons.uploadPlan')}
                        </Button>
                      </Can>
                      <Button
                        icon="u_plus"
                        onClick={handleAddNewWorkspace}
                        withLoader={isFirstPlanLoading || isSecondPlanLoading || isWorkspaceMutating}
                        disabled={isFirstPlanLoading || isSecondPlanLoading || isWorkspaceMutating || hasDraftWorkspace}
                      >
                        {t('buttons.addWorkspace')}
                      </Button>
                    </>
                  )}
                  <Can I="UPDATE" this={subject('FLOOR', {id: floorId})} passThrough>
                    {(allowed) =>
                      allowed
                        ? floorData?.floor && (
                            <Button
                              onClick={handleEditToggle}
                              icon={isEditMode ? undefined : 'u_edit-alt'}
                              iconSize={16}
                              variant="outlined"
                              color={theme.palette.generalAlt.font.tertiary}
                              iconColor={theme.palette.generalAlt.font.tertiary}
                              data-testid="floor-edit-toggle"
                              disabled={isWorkspaceMutating}
                              withLoader={isEditMode && isWorkspaceMutating}
                            >
                              {isEditMode ? t('buttons.save') : t('buttons.edit')}
                            </Button>
                          )
                        : !floorData?.isVirtual && (
                            <Button onClick={handleBookWorkspace} icon="u_bookmark" iconSize={16}>
                              {t('buttons.bookWorkspace')}
                            </Button>
                          )
                    }
                  </Can>
                  <Can I="UPDATE" this={subject('FLOOR', {id: floorId})}>
                    {isEditMode ? (
                      <Button
                        variant="outlined"
                        onClick={handleCancelClick}
                        disabled={isWorkspaceMutating}
                        withLoader={isWorkspaceMutating}
                      >
                        {t('buttons.cancel')}
                      </Button>
                    ) : null}
                  </Can>
                </Styled.ActionButtonsContainer>
              </Styled.FloorTitleRow>
            )}
            {isEditMode && isPlanExists && (
              <Styled.FloorTitleRow>
                <Can I="UPDATE" this={subject('FLOOR', {id: floorId})}>
                  <Checkbox checked={isShowSecondPlan} onChange={handleShowSecondPlan}>
                    {t('buttons.twoPlans')}
                  </Checkbox>
                </Can>
              </Styled.FloorTitleRow>
            )}
          </Styled.FloorTitleContainer>
        )
      }
    >
      {isEditMode && (
        <EmployeesList
          employees={employees}
          name={searchName}
          setEmployees={setEmployees}
          page={page}
          setPage={setPage}
          setSearchName={setSearchName}
        />
      )}
      <Styled.PlanContainer>
        <FloorAttendance
          isLoadingFirstPlan={isFirstPlanLoading || isFirstAttendanceLoading}
          freeWorkspacesCountFirstPlan={attendanceStatistics.free_workspaces}
          totalWorkspacesCountFirstPlan={attendanceStatistics.total_workspaces}
          attendancePercentFirstPlan={attendanceStatistics.attendance_percentage}
          freeWorkspacesCountSecondPlan={attendanceStatisticsSecondPlan.free_workspaces}
          totalWorkspacesCountSecondPlan={attendanceStatisticsSecondPlan.total_workspaces}
          attendancePercentSecondPlan={attendanceStatisticsSecondPlan.attendance_percentage}
          isLoadingSecondPlan={isSecondPlanLoading || isSecondAttendanceLoading}
          isShowSecondPlan={isShowSecondPlan}
        />
        <FloorSelector
          disabled={isFirstPlanLoading}
          officeId={formSelectedOfficeId}
          onFloorChanged={handleSearchFloorSubmit}
        />
        <Styled.Legend isVirtual={floorData?.isVirtual} />
        <FloorPlan
          Map={Map}
          ref={firstPlanRef}
          floorId={floorId}
          requestData={requestData}
          focusWorkspace={focusWorkspace}
          isEditMode={isEditMode}
          setEmployees={setEmployees}
          setFloorData={setFloorData}
          isShowSecondPlan={isShowSecondPlan}
          onFocusClick={focusOnFirstPlan}
          focus={focus}
          showNotifications={allowNotifications()}
          setIsPlanExists={setIsPlanExists}
          onLoadingStateChanged={setIsFirstPlanLoading}
          onAttendanceLoadingStateChanged={setIsFirstAttendanceLoading}
          onMutationPendingChanged={setIsWorkspaceMutating}
          onDraftStateChanged={handleFirstPlanDraftStateChanged}
          selectedBookingDetails={selectedBookingDetails}
          attendanceStatistics={attendanceStatistics}
          setAttendanceStatistics={setAttendanceStatistics}
        />
        {isShowSecondPlan && secondFloorId && (
          <Styled.SecondPlanContainer>
            <FloorPlan
              Map={Map}
              ref={secondPlanRef}
              floorId={secondFloorId}
              isEditMode={isEditMode}
              setEmployees={setEmployees}
              isShowSecondPlan={isShowSecondPlan}
              onFocusClick={focusOnSecondPlan}
              onLoadingStateChanged={setIsSecondPlanLoading}
              onAttendanceLoadingStateChanged={setIsSecondAttendanceLoading}
              onMutationPendingChanged={setIsWorkspaceMutating}
              onDraftStateChanged={handleSecondPlanDraftStateChanged}
              focus={!focus}
              showNotifications={allowNotifications()}
              attendanceStatistics={attendanceStatisticsSecondPlan}
              setAttendanceStatistics={setAttendanceStatisticsSecondPlan}
            />
            <FloorSelector
              disabled={isSecondPlanLoading}
              secondFloorId={secondFloorId}
              onFloorChanged={handleSecondPlanFloorChange}
            />
          </Styled.SecondPlanContainer>
        )}
      </Styled.PlanContainer>
    </Styled.FloorDetailsContainer>
  );
};

export default React.memo(FloorDetails);
